import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  adminBulkExtractV2,
  adminBulkExtractV2Status,
} from "../lib/api";

/**
 * Admin tab to trigger and monitor the v2 taxonomy bulk-extract job.
 *
 * Fires the backend background task that scrapes each approved tool's
 * URL with Claude and fills in:
 *   - instruments  (Stocks / ETFs / Mutual Funds / Options / Futures / Bonds)
 *   - regions      (North America / Europe / APAC / LATAM / MEA / Global)
 *   - audience     (retail / pro)
 *   - pro_segment  (hedge_fund / mutual_fund / ria / other)
 *
 * Polls /admin/bulk-extract-v2/status every 3 s while running so the
 * admin can watch progress. Safe to navigate away — the job keeps
 * running server-side; reopen this tab to see live status.
 */
export default function AdminBulkExtractV2() {
  const [status, setStatus] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [force, setForce] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const s = await adminBulkExtractV2Status();
      setStatus(s);
      return s;
    } catch (e) {
      // Don't toast on poll errors — could be auth flap; let next tick retry.
      return null;
    }
  };

  useEffect(() => {
    fetchStatus();
    // Poll every 3 s while running; otherwise every 15 s as a heartbeat.
    pollRef.current = setInterval(async () => {
      const s = await fetchStatus();
      if (!s?.running && pollRef.current) {
        // Slow down polling once the job has finished.
        clearInterval(pollRef.current);
        pollRef.current = setInterval(fetchStatus, 15000);
      }
    }, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const trigger = async () => {
    if (status?.running) {
      toast.error("A run is already in progress");
      return;
    }
    setTriggering(true);
    try {
      const res = await adminBulkExtractV2({ force, limit: 1000 });
      if (res.started) {
        toast.success("Background extraction started");
        // Switch to fast-poll mode immediately.
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(fetchStatus, 3000);
        await fetchStatus();
      } else {
        toast.error(`Already running: ${res.reason || ""}`);
      }
    } catch (e) {
      toast.error(`Failed to start: ${e.response?.data?.detail || e.message}`);
    } finally {
      setTriggering(false);
    }
  };

  const pct =
    status?.total > 0
      ? Math.round((status.processed / status.total) * 100)
      : 0;
  const elapsedSec =
    status?.started_at
      ? Math.round(
          ((status?.finished_at || Date.now() / 1000) - status.started_at)
        )
      : 0;
  const rate =
    elapsedSec > 0 && (status?.ok + status?.failed) > 0
      ? (status.ok + status.failed) / elapsedSec
      : 0;
  const etaMin =
    rate > 0 && status?.running
      ? Math.round((status.total - status.processed) / rate / 60)
      : null;

  return (
    <div data-testid="admin-bulk-extract-v2" className="space-y-4">
      <div className="text-sm text-[#555] leading-relaxed">
        <p>
          Re-extracts the v2 taxonomy fields for every approved tool by
          scraping the URL and asking Claude. Fills in{" "}
          <code>instruments</code>, <code>regions</code>, <code>audience</code>,
          and <code>pro_segment</code>. Runs server-side as a background
          task so it survives proxy timeouts.
        </p>
        <p className="mt-2">
          <strong>Idempotent</strong> — tools that already have all four
          fields populated are skipped. Check <code>[force]</code> to
          re-extract everything.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={trigger}
          disabled={triggering || status?.running}
          className="tv-btn-primary"
          data-testid="bulk-extract-v2-trigger"
        >
          {status?.running ? "[ running... ]" : "[ start extraction ]"}
        </button>
        <label className="text-sm cursor-pointer flex items-center gap-2">
          <input
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            data-testid="bulk-extract-v2-force"
          />
          force (re-extract complete tools too)
        </label>
        <button
          onClick={fetchStatus}
          className="tv-link"
          data-testid="bulk-extract-v2-refresh"
        >
          [refresh]
        </button>
      </div>

      {status && (
        <div
          className="border border-[#000] p-4 font-mono text-sm space-y-2"
          data-testid="bulk-extract-v2-status"
        >
          <div className="flex justify-between">
            <span>state:</span>
            <span className={status.running ? "text-[#06c]" : status.error ? "text-[#c00]" : "text-[#555]"}>
              {status.running ? "running" : status.error ? `error: ${status.error}` : status.finished_at ? "finished" : "idle"}
            </span>
          </div>
          <div className="flex justify-between">
            <span>progress:</span>
            <span>
              {status.processed} / {status.total} ({pct}%)
            </span>
          </div>
          <div className="w-full h-2 bg-[#f0f0f0] border border-[#000]">
            <div
              className="h-full bg-[#000]"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="grid grid-cols-3 gap-2 pt-2">
            <div>
              ok: <span className="text-[#080]">{status.ok}</span>
            </div>
            <div>
              failed: <span className="text-[#c00]">{status.failed}</span>
            </div>
            <div>
              skipped: <span className="text-[#888]">{status.skipped}</span>
            </div>
          </div>
          {status.current && (
            <div className="pt-2">
              <span className="text-[#555]">current:</span>{" "}
              <span className="font-bold">{status.current}</span>
            </div>
          )}
          {status.last_result && (
            <div className="text-xs text-[#555] truncate">
              last: {status.last_result}
            </div>
          )}
          <div className="pt-2 text-xs text-[#888]">
            elapsed: {Math.floor(elapsedSec / 60)}m {elapsedSec % 60}s
            {etaMin !== null && status.running ? ` · eta: ${etaMin}m` : ""}
          </div>
        </div>
      )}
    </div>
  );
}
